"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { ArrowRight, BookOpen, Calendar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function cleanExcerpt(raw?: string): string {
  if (!raw) return "";
  return raw.replace(/<[^>]*>/g, "").replace(/\[&hellip;\]/g, "...").replace(/\[\.\.\.\]/g, "...").trim();
}
function formatDate(dateString?: string, isEn?: boolean): string {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString(isEn ? "en-US" : "vi-VN", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return dateString;
  }
}
function MagazinePostsConsumer({
  isEn
}: {
  isEn: boolean;
}) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Card className="overflow-hidden border border-border">
            <Skeleton className="h-72 w-full md:h-96" />
            <CardContent className="space-y-4 p-6 md:p-8">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-8 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col gap-6 lg:col-span-5">
          {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="flex flex-col gap-4 border border-border p-4 sm:flex-row">
              <Skeleton className="h-32 w-full shrink-0 sm:w-36 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-4/5" />
                <Skeleton className="h-3 w-full" />
              </div>
            </Card>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {isEn ? "Unable to load the latest articles at this moment." : "Hiện chưa thể tải các bài viết mới nhất."}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          {isEn ? "Retry" : "Thử lại"}
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        {isEn ? "No recent articles available." : "Hiện chưa có bài viết nào được đăng tải."}
      </div>;
  }
  const primaryPost = posts[0];
  const secondaryPosts = posts.slice(1, 4);
  return <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 items-stretch">
      {/* Primary Featured Post */}
      <div className="lg:col-span-7 flex flex-col">
        <Card className="group flex-1 flex flex-col overflow-hidden border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-secondary hover:shadow-lg">
          <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
            {primaryPost.featuredImage ? <img src={primaryPost.featuredImage} alt={primaryPost.title || "Featured article"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted/60 text-muted-foreground">
                <BookOpen className="size-12 opacity-40" />
              </div>}
            {primaryPost.categories && primaryPost.categories.length > 0 && <div className="absolute top-4 left-4 z-10">
                <Badge variant="secondary" className="shadow-xs font-medium">
                  {primaryPost.categories[0].name}
                </Badge>
              </div>}
          </div>

          <CardContent className="flex flex-1 flex-col justify-between p-6 md:p-8">
            <div className="space-y-3">
              {primaryPost.date && <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                  <time dateTime={primaryPost.date}>
                    {formatDate(primaryPost.date, isEn)}
                  </time>
                </div>}

              <h3 className="font-serif text-2xl font-bold leading-tight tracking-tight text-foreground transition-colors group-hover:text-primary md:text-3xl">
                {primaryPost.link ? <Link to={primaryPost.link}>{primaryPost.title}</Link> : primaryPost.title}
              </h3>

              <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                {cleanExcerpt(primaryPost.excerpt)}
              </p>
            </div>

            {primaryPost.link && <div className="mt-6 pt-4 border-t border-border">
                <Link to={primaryPost.link} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/80">
                  <span>{isEn ? "Read more" : "Đọc tiếp"}</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>}
          </CardContent>
        </Card>
      </div>

      {/* Secondary Posts Column */}
      <div className="lg:col-span-5 flex flex-col justify-between gap-6">
        {secondaryPosts.map((post, index) => <Card key={post.id} data-index={index} className="group flex-1 flex flex-col overflow-hidden border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-secondary hover:shadow-md sm:flex-row">
            <div className="relative aspect-16/10 sm:aspect-square w-full sm:w-44 shrink-0 overflow-hidden bg-muted">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title || "Post thumbnail"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted/60 text-muted-foreground">
                  <BookOpen className="size-8 opacity-40" />
                </div>}
            </div>

            <CardContent className="flex flex-1 flex-col justify-between p-4 sm:p-5">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {post.categories && post.categories.length > 0 && <Badge variant="outline" className="text-xs">
                      {post.categories[0].name}
                    </Badge>}
                  {post.date && <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                      <time dateTime={post.date}>
                        {formatDate(post.date, isEn)}
                      </time>
                    </span>}
                </div>

                <h4 className="line-clamp-2 text-base font-semibold leading-snug text-foreground transition-colors group-hover:text-primary">
                  {post.link ? <Link to={post.link}>{post.title}</Link> : post.title}
                </h4>

                <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                  {cleanExcerpt(post.excerpt)}
                </p>
              </div>

              {post.link && <div className="mt-3">
                  <Link to={post.link} className="inline-flex items-center gap-1 text-xs font-semibold text-primary transition-colors hover:text-primary/80">
                    <span>{isEn ? "Read more" : "Đọc tiếp"}</span>
                    <ArrowRight className="size-3 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>}
            </CardContent>
          </Card>)}
      </div>
    </div>;
}
export default function LatestInsights() {
  const [isEn, setIsEn] = useState(false);
  useEffect(() => {
    const checkLang = () => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        setIsEn(stored === "en");
      } catch {
        setIsEn(false);
      }
    };
    checkLang();
    const handleCustomChange = () => checkLang();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang") checkLang();
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const wpQuery = new WP_Query({
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc",
    status: ["publish"]
  });
  return <section id="latestinsights" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-border pb-8">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2">
              <span className="h-1.5 w-6 rounded-full bg-secondary" />
              <span className="text-xs font-bold tracking-wider uppercase text-secondary">
                {isEn ? "Blog & Resources" : "Góc Chuyên Gia & Tin Tức"}
              </span>
            </div>
            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {isEn ? "Insights & News" : "Kiến thức & Tin tức"}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {isEn ? "Share knowledge about Robotics, STEM, LEGO Robotics, and hands-on learning activities for parents." : "Chia sẻ kiến thức về Robotics, STEM, LEGO Robotics và các hoạt động học tập thực tiễn cùng con cho phụ huynh."}
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="border-border hover:border-secondary">
              <Link to="#">
                {isEn ? "View All Articles" : "Xem tất cả bài viết"}
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Dynamic Posts Consumer */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 4,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }}>
          <MagazinePostsConsumer isEn={isEn} />
        </PostsProvider>
      </div>
    </section>;
}